/**
 * FinalNightComposerPage ✦ — iter201
 * ─────────────────────────────────────────────────────────────
 * Operator-only Chronicle composer surface.
 *
 * The brief locks the restraint contract:
 *   • NO configurator. NO drag/drop. NO template gallery.
 *   • NO loading percentages, NO "AI is generating…", NO spinners.
 *   • At most: a single "Regenerate ✦" gesture.
 *
 * This page calls /api/events/{event_id}/final-night/compose, shows
 * the structured page plan as a quiet editorial preview, and offers
 * the rendered PDF for the operator to inspect or print. That is
 * all. Composition lives in the backend service layer; this surface
 * is glass — it must read as authored, never assembled.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK = '#1f1409';
const INK_SOFT = 'rgba(31,20,9,0.72)';
const INK_FAINT = 'rgba(31,20,9,0.55)';
const PARCHMENT = '#f5efdf';
const IVORY = '#fbf6ec';
const GOLD = '#a4824a';
const RULE = 'rgba(31,20,9,0.14)';
const FONT_DISPLAY = "'Cormorant Garamond','Playfair Display',Georgia,serif";
const FONT_BODY = "'Inter',system-ui,sans-serif";

// Resolve a media URL — backend returns `/api/photos/<storage_path>`
// already, but we still need to absolute it for cross-origin loads.
const resolveMedia = (rel) => {
  if (!rel) return null;
  if (rel.startsWith('http')) return rel;
  if (rel.startsWith('/api/')) return `${BACKEND_URL}${rel}`;
  return `${BACKEND_URL}/api/photos/${rel}`;
};

const SectionEyebrow = ({ children, testId }) => (
  <p
    data-testid={testId}
    style={{
      margin: 0, fontFamily: FONT_BODY, fontSize: 11,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: INK_FAINT,
    }}
  >
    {children}
  </p>
);

const PagePreviewFront = ({ page }) => {
  const lead = page?.lead_photo ? resolveMedia(page.lead_photo.url || page.lead_photo.storage_path) : null;
  return (
    <article data-testid="final-night-preview-page-1" style={{
      background: IVORY, padding: '36px 32px',
      border: `1px solid ${RULE}`,
      boxShadow: '0 1px 0 rgba(31,20,9,0.04)',
    }}>
      <SectionEyebrow>The front page ✦</SectionEyebrow>
      <h2 style={{
        margin: '16px 0 4px', fontFamily: FONT_DISPLAY, fontWeight: 500,
        fontSize: 'clamp(28px,3.6vw,42px)', lineHeight: 1.05,
        letterSpacing: '-0.015em', color: INK,
      }}>{page.headline}</h2>
      {page.subheadline && (
        <p style={{
          margin: 0, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 14, color: INK_SOFT,
        }}>{page.subheadline}</p>
      )}
      {lead && (
        <img
          data-testid="final-night-preview-hero"
          alt=""
          src={lead}
          style={{
            display: 'block', width: '100%', marginTop: 22,
            aspectRatio: '4 / 5', objectFit: 'cover',
            filter: 'grayscale(1) sepia(0.18)',
            boxShadow: '0 12px 30px rgba(31,20,9,0.18)',
          }}
        />
      )}
      {page.photos?.length > 0 && (
        <div style={{
          marginTop: 16, display: 'grid', gap: 6,
          gridTemplateColumns: `repeat(${Math.min(5, page.photos.length)}, 1fr)`,
        }}>
          {page.photos.slice(0, 5).map((p, i) => p && (
            <img
              key={p.photo_id || i}
              alt=""
              src={resolveMedia(p.url || p.storage_path)}
              style={{
                width: '100%', aspectRatio: '1 / 1.1',
                objectFit: 'cover', filter: 'grayscale(1) sepia(0.18)',
              }}
            />
          ))}
        </div>
      )}
    </article>
  );
};

const PagePreviewRoom = ({ page }) => (
  <article data-testid={`final-night-preview-page-${page.page_no}`} style={{
    background: IVORY, padding: '32px 30px',
    border: `1px solid ${RULE}`,
  }}>
    <SectionEyebrow>{page.headline}</SectionEyebrow>
    <div style={{
      marginTop: 18, display: 'grid', gap: 8,
      gridTemplateColumns: 'repeat(2, 1fr)',
    }}>
      {page.photos?.map((p, i) => p && (
        <img
          key={p.photo_id || i}
          alt=""
          src={resolveMedia(p.url || p.storage_path)}
          style={{
            width: '100%', aspectRatio: '4 / 3.4',
            objectFit: 'cover', filter: 'grayscale(1) sepia(0.18)',
            transform: i % 5 === 4 ? 'rotate(-0.6deg)' : 'rotate(0.4deg)',
          }}
        />
      ))}
    </div>
    {page.fragments?.length > 0 && (
      <ul data-testid="final-night-preview-fragments" style={{
        listStyle: 'none', padding: 0, margin: '28px 0 0',
        borderTop: `1px solid ${RULE}`,
      }}>
        {page.fragments.map((f, i) => (
          <li
            key={i}
            data-testid="final-night-preview-fragment"
            style={{
              padding: '14px 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 18, color: INK_SOFT, lineHeight: 1.45,
              borderBottom: `1px solid ${RULE}`,
            }}
          >
            {f}
          </li>
        ))}
      </ul>
    )}
  </article>
);

const PagePreviewGroups = ({ page }) => (
  <article data-testid="final-night-preview-page-3" style={{
    background: IVORY, padding: '32px 30px',
    border: `1px solid ${RULE}`,
  }}>
    <SectionEyebrow>{page.headline}</SectionEyebrow>
    {(page.moments || []).map((m, i) => (
      <section
        key={i}
        data-testid="final-night-preview-moment"
        style={{ marginTop: 26 }}
      >
        <h3 style={{
          margin: 0, fontFamily: FONT_DISPLAY, fontWeight: 500,
          fontSize: 22, color: INK, letterSpacing: '-0.005em',
        }}>{m.label}</h3>
        <div style={{
          marginTop: 10, display: 'grid', gap: 6,
          gridTemplateColumns: `repeat(${Math.min(4, m.photos.length)}, 1fr)`,
        }}>
          {m.photos?.map((p, j) => p && (
            <img
              key={p.photo_id || j}
              alt=""
              src={resolveMedia(p.url || p.storage_path)}
              style={{
                width: '100%', aspectRatio: '1 / 1.1',
                objectFit: 'cover', filter: 'grayscale(1) sepia(0.18)',
              }}
            />
          ))}
        </div>
      </section>
    ))}
  </article>
);

const PagePreviewClosing = ({ page }) => {
  const lead = page?.lead_photo ? resolveMedia(page.lead_photo.url || page.lead_photo.storage_path) : null;
  return (
    <article data-testid="final-night-preview-page-4" style={{
      background: IVORY, padding: '32px 30px',
      border: `1px solid ${RULE}`,
      textAlign: 'center',
    }}>
      {lead && (
        <img
          alt=""
          src={lead}
          style={{
            display: 'block', width: '100%',
            aspectRatio: '4 / 5', objectFit: 'cover',
            filter: 'grayscale(1) sepia(0.18)',
            boxShadow: '0 12px 30px rgba(31,20,9,0.18)',
          }}
        />
      )}
      {page.closing_line && (
        <p
          data-testid="final-night-preview-closing-line"
          style={{
            margin: '38px auto 0', maxWidth: 420,
            fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 'clamp(22px,2.6vw,28px)', color: INK,
            lineHeight: 1.35,
          }}
        >
          {page.closing_line}
        </p>
      )}
      <p style={{
        margin: '36px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
        fontSize: 12, color: INK_FAINT, letterSpacing: '0.16em',
        textTransform: 'uppercase',
      }}>
        EventDrop ✦ The Final Night
      </p>
    </article>
  );
};

const PagePreview = ({ page }) => {
  if (page.kind === 'front') return <PagePreviewFront page={page} />;
  if (page.kind === 'groups') return <PagePreviewGroups page={page} />;
  if (page.kind === 'closing') return <PagePreviewClosing page={page} />;
  return <PagePreviewRoom page={page} />;
};

const FinalNightComposerPage = () => {
  const { slug } = useParams();
  const [eventId, setEventId] = useState(null);
  const [eventName, setEventName] = useState(null);
  const [plan, setPlan] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [status, setStatus] = useState('resolving'); // resolving | composing | ready | gathering | error
  const [errorLine, setErrorLine] = useState('');

  // Resolve slug → event_id once.
  useEffect(() => {
    if (!slug) return undefined;
    let aborted = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (aborted) return;
        setEventId(r.data?.event_id);
        setEventName(r.data?.event_name || null);
        setStatus('idle');
      } catch {
        if (!aborted) {
          setStatus('error');
          setErrorLine('No evening found here ✦');
        }
      }
    })();
    return () => { aborted = true; };
  }, [slug]);

  const compose = useCallback(async () => {
    if (!eventId) return;
    setStatus('composing');
    setErrorLine('');
    try {
      // iter202 — distinguish first-arrival auto-compose from the
      // explicit operator `Regenerate ✦` gesture. The first call
      // lets the artefact store serve a durable cache hit; the
      // second forces a fresh render.
      const force = status === 'ready' || status === 'gathering';
      const r = await axios.post(
        `${API}/events/${eventId}/final-night/compose`,
        null,
        { params: force ? { regenerate: true } : {} },
      );
      if (r.data?.status === 'settled' && r.data?.pdf_url) {
        setPlan(r.data.plan);
        // Cache-bust the PDF link so a regenerate reload picks up
        // the new durable bytes instead of the previously-streamed
        // copy.
        setPdfUrl(`${BACKEND_URL}${r.data.pdf_url}?t=${Date.now()}`);
        setStatus('ready');
      } else if (r.data?.status === 'gathering') {
        setPlan(r.data.plan || null);
        setPdfUrl(null);
        setStatus('gathering');
      } else {
        // Defensive: any unknown shape reads as gathering — never
        // engineering language.
        setStatus('gathering');
      }
    } catch (e) {
      setStatus('error');
      setErrorLine(e?.response?.data?.detail || 'The Chronicle is still gathering itself ✦');
    }
  }, [eventId, status]);

  // Compose once on first arrival so the operator lands on a real
  // editorial preview without an extra gesture. They can then
  // re-press "Regenerate ✦" — the brief permits exactly that.
  useEffect(() => {
    if (status === 'idle' && eventId) {
      compose();
    }
  }, [status, eventId, compose]);

  const statusLine = useMemo(() => {
    if (status === 'resolving') return 'Finding the evening ✦';
    if (status === 'composing') return 'The Chronicle is forming ✦';
    if (status === 'gathering') return 'The Chronicle is still gathering itself ✦';
    if (status === 'error') return errorLine || 'The Chronicle is still gathering itself ✦';
    if (status === 'ready') return 'The Chronicle has settled ✦';
    return '';
  }, [status, errorLine]);

  return (
    <div
      data-testid="final-night-composer-page"
      data-status={status}
      style={{
        minHeight: '100vh', background: PARCHMENT, color: INK,
        fontFamily: FONT_BODY, padding: '24px 22px 80px',
      }}
    >
      <BackButton
        fallbackHref={`/event/${slug}`}
        label="Back"
        variant="on-light"
        testId="final-night-composer-back-btn"
      />

      <header style={{ maxWidth: 720, margin: '40px auto 0' }}>
        <SectionEyebrow testId="final-night-composer-eyebrow">
          PRESERVE THE NIGHT ✦  ·  OPERATOR
        </SectionEyebrow>
        <h1
          data-testid="final-night-composer-title"
          style={{
            margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontWeight: 500,
            fontSize: 'clamp(38px,5.4vw,56px)', lineHeight: 1.05,
            letterSpacing: '-0.015em', color: INK,
          }}
        >
          The Final Night ✦
        </h1>
        {eventName && (
          <p style={{
            margin: '10px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 16, color: INK_SOFT,
          }}>{eventName}</p>
        )}
        <p
          data-testid="final-night-composer-status"
          style={{
            margin: '22px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 'clamp(17px,2vw,20px)', color: GOLD,
          }}
        >
          {statusLine}
        </p>

        {/* The brief permits at most a single "Regenerate ✦" gesture
            and the PDF preview link. No template gallery, no editing
            controls, no advanced toggles. */}
        <div style={{
          marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap',
        }}>
          <button
            type="button"
            data-testid="final-night-regenerate-btn"
            onClick={compose}
            disabled={status === 'composing' || !eventId}
            style={{
              padding: '12px 22px', borderRadius: 4,
              border: `1px solid ${GOLD}`,
              background: status === 'composing' ? 'rgba(164,130,74,0.12)' : 'transparent',
              color: INK, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 15, letterSpacing: '0.02em',
              cursor: status === 'composing' || !eventId ? 'default' : 'pointer',
            }}
          >
            Regenerate ✦
          </button>
          {pdfUrl && (
            <a
              data-testid="final-night-pdf-link"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '12px 22px', borderRadius: 4,
                border: `1px solid ${RULE}`,
                background: IVORY,
                color: INK, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
                fontSize: 15, letterSpacing: '0.02em',
                textDecoration: 'none',
              }}
            >
              Open archival edition ✦
            </a>
          )}
        </div>
      </header>

      {/* Editorial preview — same shape as the PDF the operator
          opens, but rendered as quiet HTML for in-room reading. */}
      {plan && (
        <main
          data-testid="final-night-composer-preview"
          style={{
            maxWidth: 720, margin: '48px auto 0',
            display: 'flex', flexDirection: 'column', gap: 24,
          }}
        >
          {plan.pages.map((page) => (
            <PagePreview key={page.page_no} page={page} />
          ))}
        </main>
      )}
    </div>
  );
};

export default FinalNightComposerPage;
